/**
 * Shared onboarding wizard primitives.
 *
 * Both the personal-invite flow (/onboard/:token) and the shareable-link
 * flow (/onboard/link/:token) render the same wizard shell, the same
 * step bodies, and the same terminal screens — only data loading,
 * submission, branding, and the documents step differ.
 *
 * Exports:
 *   EMPTY_PAYLOAD     – starting state for the form
 *   mergePayload      – defensive merge for hydration from server drafts
 *   errMessage        – pulls a user-friendly message off an axios error
 *   Field             – labeled input
 *   StepWelcome       – the first step (parameterized for branding)
 *   StepContact       – first/last/email/phone
 *   StepVehicle       – licence plate + optional nickname
 *   StepReview        – review + declaration + submit
 *   TerminalScreen    – success/warn/info card
 *   WizardChrome      – brandbar + header + stepper + step card + Back/Next
 *
 * The wizard never reaches into the network layer; orchestrators wire up
 * load/save/submit and pass handlers in. That's the single seam between
 * personal-invite and shareable-link.
 */

import React from 'react';
import SharedLinkBrandHeader from './SharedLinkBrandHeader.jsx';
import PhoneField from '../ui/PhoneField/PhoneField.jsx';
import { formatPhoneDisplay } from '../../utils/phone.js';

// Non-component utilities (EMPTY_PAYLOAD / mergePayload / errMessage) live
// in onboardingShared.js so React Fast Refresh stays happy with this JSX
// module — Vite refuses to refresh a file that mixes components and
// constants.

// ── Field input ──────────────────────────────────────────────────────

export function Field({ label, value, onChange, type = 'text', required = false, ...rest }) {
  return (
    <label className="ob-field">
      <span className="ob-field__label">
        {label}{required && <span className="ob-required">*</span>}
      </span>
      <input
        className="ob-field__input"
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
    </label>
  );
}

// ── Step bodies ──────────────────────────────────────────────────────

/**
 * StepWelcome is parameterized so a client-branded shared link can say
 * "Welcome to W Plaza" while the personal-invite flow stays "Welcome to
 * GreenCharge". Bullets default to the four standard ones; pass
 * `extraBullets` to insert a Documents bullet for invites that ask for them.
 */
export function StepWelcome({
  welcomeTitle = 'Welcome to GreenCharge',
  intro,
  expiresAt,
  extraBullets = [],
}) {
  const defaultIntro =
    "You've been invited to register your vehicle for charging. This should take under a minute.";
  return (
    <>
      <h2>{welcomeTitle}</h2>
      <p>{intro || defaultIntro}</p>
      {expiresAt && (
        <p className="ob-subtle">
          This link expires on {new Date(expiresAt).toLocaleDateString()}.
        </p>
      )}
      <ul className="ob-bullets">
        <li>Confirm your contact details.</li>
        <li>Add your vehicle's licence plate.</li>
        {extraBullets.map((b) => <li key={b}>{b}</li>)}
        <li>Review and submit.</li>
      </ul>
    </>
  );
}

export function StepContact({ payload, updateField }) {
  return (
    <>
      <h2>Your contact details</h2>
      <div className="ob-grid-2">
        <Field
          label="First name"
          value={payload.contact.firstName}
          onChange={(v) => updateField('contact', 'firstName', v)}
          required
        />
        <Field
          label="Last name"
          value={payload.contact.lastName}
          onChange={(v) => updateField('contact', 'lastName', v)}
          required
        />
      </div>
      <Field
        label="Email"
        type="email"
        value={payload.contact.email}
        onChange={(v) => updateField('contact', 'email', v)}
        required
      />
      {/* The onboarding pages are light in both themes, so the shared
          field is pinned to the light tokens here. A valid number is kept
          as E.164; anything else is kept as typed so it is never lost. */}
      <div className="ob-phone" data-theme="light">
        <PhoneField
          label="Phone"
          name="phone"
          value={payload.contact.phone}
          onChange={(p) => updateField('contact', 'phone', p.valid ? p.e164 : p.raw)}
        />
      </div>
      <Field
        label="Address (optional)"
        value={payload.contact.address}
        onChange={(v) => updateField('contact', 'address', v)}
      />
    </>
  );
}

export function StepVehicle({ payload, updateField, vehicleHint }) {
  return (
    <>
      <h2>Vehicle</h2>
      <p className="ob-subtle">
        {vehicleHint || 'We only need your licence plate. Everything else is optional.'}
      </p>
      <Field
        label="Licence plate"
        value={payload.vehicle.plate}
        onChange={(v) => updateField('vehicle', 'plate', v)}
        required
        autoFocus
        autoCapitalize="characters"
      />
      <Field
        label="Nickname (optional)"
        value={payload.vehicle.vehicleName}
        onChange={(v) => updateField('vehicle', 'vehicleName', v)}
      />
      <Field
        label="Make (optional)"
        placeholder="e.g. BMW"
        value={payload.vehicle.make || ''}
        onChange={(v) => updateField('vehicle', 'make', v)}
      />
      <Field
        label="Model (optional)"
        placeholder="e.g. 116i"
        value={payload.vehicle.model || ''}
        onChange={(v) => updateField('vehicle', 'model', v)}
      />
      <Field
        label="Year (optional)"
        placeholder="e.g. 2020"
        inputMode="numeric"
        value={payload.vehicle.year != null ? String(payload.vehicle.year) : ''}
        onChange={(v) => updateField('vehicle', 'year', v)}
      />
      <Field
        label="Colour (optional)"
        placeholder="e.g. Silver"
        value={payload.vehicle.color || ''}
        onChange={(v) => updateField('vehicle', 'color', v)}
      />
    </>
  );
}

export function StepReview({
  payload,
  documents = [],
  requestedDocuments,
  submitError,
  submitting,
  validationErrors,
  onSubmit,
  onJumpToStep,
  setAgreedToTerms,
  submitLabel = 'Submit for review',
}) {
  const docsRequested =
    Array.isArray(requestedDocuments) && requestedDocuments.length > 0;
  const missing = validationErrors || [];
  const missingByStep = new Set(missing.map((m) => m.stepId));
  const sectionFlag = (stepId) =>
    missingByStep.has(stepId) ? ' ob-review-section--invalid' : '';

  return (
    <>
      <h2>Review & submit</h2>
      <p className="ob-subtle">
        Check your details below, confirm the declaration, and submit. An administrator
        will review your information and contact you.
      </p>

      <h3 className={`ob-review-heading${sectionFlag('contact')}`}>Contact</h3>
      <dl className="ob-review-grid">
        <dt>First name</dt><dd>{payload.contact.firstName || '—'}</dd>
        <dt>Last name</dt><dd>{payload.contact.lastName || '—'}</dd>
        <dt>Email</dt><dd>{payload.contact.email || '—'}</dd>
        <dt>Phone</dt><dd>{formatPhoneDisplay(payload.contact.phone) || '—'}</dd>
      </dl>

      <h3 className={`ob-review-heading${sectionFlag('vehicle')}`}>Vehicle</h3>
      <dl className="ob-review-grid">
        <dt>Licence plate</dt><dd>{payload.vehicle.plate || '—'}</dd>
        {payload.vehicle.vehicleName && (
          <>
            <dt>Nickname</dt><dd>{payload.vehicle.vehicleName}</dd>
          </>
        )}
        {payload.vehicle.make && (
          <>
            <dt>Make</dt><dd>{payload.vehicle.make}</dd>
          </>
        )}
        {payload.vehicle.model && (
          <>
            <dt>Model</dt><dd>{payload.vehicle.model}</dd>
          </>
        )}
        {payload.vehicle.year && (
          <>
            <dt>Year</dt><dd>{payload.vehicle.year}</dd>
          </>
        )}
        {payload.vehicle.color && (
          <>
            <dt>Colour</dt><dd>{payload.vehicle.color}</dd>
          </>
        )}
      </dl>

      {docsRequested && (
        <>
          <h3>Documents</h3>
          <p className="ob-subtle">
            {documents.length === 0
              ? 'No documents uploaded.'
              : `${documents.length} file(s) uploaded`}
          </p>
        </>
      )}

      <label
        className={`ob-field ob-field--checkbox${
          missingByStep.has('review') ? ' ob-field--invalid' : ''
        }`}
        style={{ marginTop: 16 }}
      >
        <input
          type="checkbox"
          checked={!!payload.agreedToTerms}
          onChange={(e) => setAgreedToTerms(e.target.checked)}
        />
        <span>
          I confirm that the information provided is accurate and that I am authorised
          to register this vehicle.
        </span>
      </label>

      {missing.length > 0 && (
        <div className="ob-alert ob-alert--warn" role="alert" style={{ marginTop: 14 }}>
          <strong>Please complete the missing required details before submitting.</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {missing.map((m) => (
              <li key={m.key} style={{ margin: '2px 0' }}>
                <button
                  type="button"
                  className="ob-link-btn"
                  onClick={() => onJumpToStep?.(m.stepId)}
                >
                  {m.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {submitError && (
        <p className="ob-alert ob-alert--danger" style={{ marginTop: 14 }}>
          {submitError}
        </p>
      )}

      <div style={{ marginTop: 16 }}>
        <button
          className="ob-btn ob-btn--primary ob-btn--large"
          onClick={onSubmit}
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : submitLabel}
        </button>
      </div>
    </>
  );
}

// ── Terminal screen ──────────────────────────────────────────────────

export function TerminalScreen({ title, message, kind = 'info', branding = null, brandTag }) {
  return (
    <div className={`ob-shell ob-shell--terminal ob-shell--${kind}`}>
      <div className="ob-container">
        <SharedLinkBrandHeader branding={branding} tag={brandTag} />
        <div className="ob-card ob-card--centered">
          <h1>{title}</h1>
          <p>{message}</p>
        </div>
      </div>
    </div>
  );
}

// ── Wizard chrome ────────────────────────────────────────────────────

/**
 * Surrounding shell every step renders into. Owns the brandbar, the page
 * header H1, the stepper rail, the step card, and the Back / Next /
 * save-status footer. The body of the current step is passed as children.
 *
 * Branding: pass a `branding` object to enable the SharedLinkBrandHeader
 * variant (client logo / name + small "Powered by GreenCharge"). Pass
 * null for the plain GreenCharge brandbar used by the personal-invite
 * flow. `brandTag` lets the personal-invite flow keep its "Onboarding"
 * tag next to the wordmark.
 */
export function WizardChrome({
  branding = null,
  brandTag,
  headerTitle,
  headerSubtitle,
  steps,
  step,
  savingDraft,
  savedAt,
  onBack,
  onNext,
  showNext,
  children,
}) {
  return (
    <div className="ob-shell">
      <div className="ob-container">
        <SharedLinkBrandHeader branding={branding} tag={brandTag} />

        <header className="ob-header">
          <h1>{headerTitle}</h1>
          {headerSubtitle && <p className="ob-subtle">{headerSubtitle}</p>}
        </header>

        <ol className="ob-stepper" aria-label="Progress">
          {steps.map((s) => (
            <li
              key={s.id}
              className={`ob-stepper__item ${s.number === step ? 'ob-stepper__item--active' : ''} ${s.number < step ? 'ob-stepper__item--done' : ''}`}
            >
              <span className="ob-stepper__num">{s.number}</span>
              <span className="ob-stepper__label">{s.label}</span>
            </li>
          ))}
        </ol>

        <div className="ob-card">{children}</div>

        <div className="ob-controls">
          <div className="ob-save-status">
            {savingDraft && 'Saving…'}
            {!savingDraft && savedAt && `Saved ${savedAt.toLocaleTimeString()}`}
          </div>
          <div className="ob-controls-buttons">
            <button className="ob-btn ob-btn--ghost" disabled={step === 1} onClick={onBack}>
              Back
            </button>
            {showNext && (
              <button className="ob-btn ob-btn--primary" onClick={onNext}>
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
